import type React from 'react';
import {
  BookmarkIcon,
  FlagIcon,
  PinIcon,
  RotateCcwIcon,
  SquarePenIcon,
  TimerOffIcon,
  Trash2Icon,
} from 'lucide-react';
import { DropdownMenuItem } from '@components/ui/dropdown-menu';
import { POLL_COPY } from './pollCopy';
import type { PollMenuRow } from './pollMenuRows';

export type PollMenuHandlers = Record<PollMenuRow, (event: React.MouseEvent) => void>;

// mb-gray-dark = the frames' mb-gray-700 #484848 (E1); focus one step darker, mb-charcoal #3D3D3D.
const PLAIN = 'text-mb-gray-dark focus:text-mb-charcoal focus:bg-gray-50 cursor-pointer';
// Only Delete is red (RESOLUTIONS R14; the frames draw Report red too, R14 wins).
const RED = 'text-red-600 focus:text-red-700 focus:bg-red-50 cursor-pointer';
const ICON = 'w-4 h-4 mr-2';

// E1: Lucide `pin` and `square-pen` (the post menu's own Pin keeps PinActionIcon).
const ROW_LOOK: Record<PollMenuRow, { label: string; className: string; icon: React.ReactNode }> = {
  pin: { label: POLL_COPY.menuPinPoll, className: PLAIN, icon: <PinIcon className={ICON} /> },
  unpin: {
    label: POLL_COPY.menuUnpinPoll,
    className: PLAIN,
    icon: <PinIcon className={ICON} />,
  },
  bookmark: {
    label: POLL_COPY.menuBookmark,
    className: PLAIN,
    icon: <BookmarkIcon className={ICON} />,
  },
  edit: {
    label: POLL_COPY.menuEditPoll,
    className: PLAIN,
    icon: <SquarePenIcon className={ICON} />,
  },
  end: {
    label: POLL_COPY.menuEndPollNow,
    className: PLAIN,
    icon: <TimerOffIcon className={ICON} />,
  },
  reopen: {
    label: POLL_COPY.menuReopenPoll,
    className: PLAIN,
    icon: <RotateCcwIcon className={ICON} />,
  },
  report: { label: POLL_COPY.menuReport, className: PLAIN, icon: <FlagIcon className={ICON} /> },
  delete: { label: POLL_COPY.menuDelete, className: RED, icon: <Trash2Icon className={ICON} /> },
};

/**
 * The rows of `pollMenuRows` (or a live poll's `liveMenuRows`), each wired to the card's own handler
 * (today's Pin, Report, …). `labels` renames a row where a live poll's words differ ("Edit").
 */
export const PollMenuItems: React.FC<{
  rows: readonly PollMenuRow[];
  handlers: PollMenuHandlers;
  labels?: Partial<Record<PollMenuRow, string>>;
}> = ({ rows, handlers, labels }) => (
  <>
    {rows.map(row => (
      <DropdownMenuItem key={row} onClick={handlers[row]} className={ROW_LOOK[row].className}>
        {ROW_LOOK[row].icon}
        {labels?.[row] ?? ROW_LOOK[row].label}
      </DropdownMenuItem>
    ))}
  </>
);
